import React, { useEffect, useState } from "react";
import { http } from "../lib/api";
import { ArrowRight, CheckCircle, Sparkle, Lightning } from "@phosphor-icons/react";

const HERO_BG =
  "https://images.pexels.com/photos/13219418/pexels-photo-13219418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Welcome({ onNavigate }) {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    http.get("/insights/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const stats = summary || { totalCandidates: 0, averageMatchScore: 0, openRequisitions: 0 };

  return (
    <div className="space-y-6" data-testid="welcome-page">
      {/* Hero */}
      <div
        className="relative overflow-hidden rounded-sm border"
        style={{
          backgroundImage: `linear-gradient(115deg, rgba(10,10,10,0.78) 0%, rgba(0,47,167,0.62) 65%, rgba(0,47,167,0.35) 100%), url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="px-8 md:px-12 py-12 md:py-16 text-white max-w-3xl">
          <div className="label-eyebrow text-white/70 mb-3">
            Vaku HCM · GenAI Agents
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight">
            Agentic AI for Modern Recruitment
          </h1>
          <p className="mt-3 text-white/85 text-lg max-w-xl">
            Job Requisition to Complete Pre-Hire Tasks — orchestrated end-to-end by Vaku agents.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              data-testid="welcome-cta-hr-chat"
              className="btn btn-primary"
              style={{ background: "#FFFFFF", color: "#002FA7" }}
              onClick={() => onNavigate("hr-chat")}
            >
              <Sparkle size={16} weight="fill" /> Chat with HR Agent
            </button>
            <button
              data-testid="welcome-cta-requisition"
              className="btn btn-outline"
              style={{ borderColor: "rgba(255,255,255,0.5)", color: "white" }}
              onClick={() => onNavigate("requisitions-manage")}
            >
              Manage Requisitions <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card card-hover p-5" data-testid="stat-candidates">
          <div className="label-eyebrow mb-1.5">Total Candidates</div>
          <div className="font-display text-3xl font-bold">{stats.totalCandidates}</div>
          <div className="text-xs text-slate-500 mt-1">across all open requisitions</div>
        </div>
        <div className="card card-hover p-5" data-testid="stat-avg-match">
          <div className="label-eyebrow mb-1.5">Avg. Match Score</div>
          <div className="font-display text-3xl font-bold">
            {stats.averageMatchScore}<span className="text-slate-400 text-xl">/100</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">AI-evaluated fit</div>
        </div>
        <div className="card card-hover p-5" data-testid="stat-open-reqs">
          <div className="label-eyebrow mb-1.5">Open Requisitions</div>
          <div className="font-display text-3xl font-bold">{stats.openRequisitions}</div>
          <div className="text-xs text-slate-500 mt-1">currently hiring</div>
        </div>
      </div>

      {/* Capabilities */}
      <div className="card p-6">
        <div className="flex items-center gap-2 mb-5">
          <Lightning size={18} weight="fill" className="text-[#002FA7]" />
          <h2 className="font-display text-xl font-bold">
            AI-Powered Talent Acquisition: Multi-Agent System From Sourcing to Pre-Hire
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3">
          {[
            "End-to-end Requisition management (Draft → Open → Closed)",
            "Creates Campaigns & Candidate Pools",
            "Manages Candidate Responses & Interview Activity",
            "Conducts AI Video Interviews & Collects Feedback",
            "Auto-evaluates CVs against requisition criteria",
            "Triggers compliance & policy checks before posting",
            "Streams structured Job Descriptions on demand",
            "Drafts interview questions with scoring rubrics",
          ].map((line) => (
            <div key={line} className="flex items-start gap-2 text-sm text-slate-700">
              <CheckCircle size={16} weight="fill" className="mt-0.5 text-emerald-600 flex-shrink-0" />
              <span>{line}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
